import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useFirstClientDeathId } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/utilityHooks';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';
import { PulidKind } from '@/utils/pulid';

export interface UseOppositeDeathOrderSuggestionsResult {
  /** Whether the opposite death order has unacknowledged suggestions */
  hasOppositeDeathOrderSuggestions: boolean;
  /** Count of unacknowledged suggestions for the opposite death order */
  oppositeDeathOrderSuggestionsCount: number;
  /** First name of the client who dies first in the opposite death order */
  oppositeDeathOrderClientName: string;
  /** ID of the client who dies first in the opposite death order */
  oppositeDeathOrderClientId: string | null;
  /** Whether the opposite death order is the "primary" (first client in primaryClients array) */
  isOppositeDeathOrderPrimary: boolean;
}

/**
 * Hook to calculate whether the opposite death order has unacknowledged
 * dispositive provision suggestions.
 *
 * This is used to show a callout when viewing a death order with no suggestions
 * but the opposite death order has suggestions.
 */
export function useOppositeDeathOrderSuggestions(): UseOppositeDeathOrderSuggestionsResult {
  const { primaryClients, isTwoClientHousehold } = useHouseholdDetailsContext();
  const { suggestionsByKind, targetKind } =
    useDispositiveProvisionSuggestionsContext();
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();
  const firstDeathClientId = useFirstClientDeathId();

  return useMemo(() => {
    // Default result for single-client households or missing data
    const defaultResult: UseOppositeDeathOrderSuggestionsResult = {
      hasOppositeDeathOrderSuggestions: false,
      oppositeDeathOrderSuggestionsCount: 0,
      oppositeDeathOrderClientName: '',
      oppositeDeathOrderClientId: null,
      isOppositeDeathOrderPrimary: false,
    };

    // Only applicable for two-client households with entity targets
    if (
      !isTwoClientHousehold ||
      !primaryClients ||
      primaryClients.length < 2 ||
      targetKind !== PulidKind.Entity
    ) {
      return defaultResult;
    }

    // Determine which client is the "opposite" death order
    const currentDeathClientIndex = primaryClients.findIndex(
      (c) => c.id === firstDeathClientId
    );

    if (currentDeathClientIndex === -1) {
      return defaultResult;
    }

    const oppositeClientIndex = currentDeathClientIndex === 0 ? 1 : 0;
    const oppositeClient = primaryClients[oppositeClientIndex];

    if (!oppositeClient) {
      return defaultResult;
    }

    // Determine suggestions for the opposite death order.
    // KGV2 suggestions are keyed by dpFirstDeathGrantorClientProfile instead
    // of grantor-indexed suggestion kinds.
    const legacyOppositeSuggestionKind =
      oppositeClientIndex === 0
        ? AiSuggestionKind.DpRecipientGrantor_1DiesFirst
        : AiSuggestionKind.DpRecipientGrantor_2DiesFirst;
    const legacyOppositeSuggestions =
      suggestionsByKind[legacyOppositeSuggestionKind] ?? [];
    const v2OppositeSuggestions = (
      suggestionsByKind[AiSuggestionKind.DpRecipientV2] ?? []
    ).filter((suggestion) => {
      return (
        suggestion.dpFirstDeathGrantorClientProfile?.id === oppositeClient.id
      );
    });

    // Keep behavior consistent with DPFormSuggestionsProvider:
    // if no V2 suggestions exist for this death order, fall back
    // to the legacy grantor-indexed kinds.
    const oppositeSuggestions = !isEmpty(v2OppositeSuggestions)
      ? v2OppositeSuggestions
      : legacyOppositeSuggestions;

    // Filter to only unacknowledged recipient suggestions
    const unacknowledgedSuggestions = oppositeSuggestions.filter(
      (s) => s.dpRecipient && !acknowledgedSuggestions[s.id]
    );

    return {
      hasOppositeDeathOrderSuggestions: unacknowledgedSuggestions.length > 0,
      oppositeDeathOrderSuggestionsCount: unacknowledgedSuggestions.length,
      oppositeDeathOrderClientName: oppositeClient.displayGivenName ?? '',
      oppositeDeathOrderClientId: oppositeClient.id,
      // Primary death order is when the first client (index 0) dies first
      isOppositeDeathOrderPrimary: oppositeClientIndex === 0,
    };
  }, [
    acknowledgedSuggestions,
    firstDeathClientId,
    isTwoClientHousehold,
    primaryClients,
    suggestionsByKind,
    targetKind,
  ]);
}
